import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppFooter from "./Homepage/AppFooter";
import SlotBookingModal from "../pages/SlotBookingModal"; // Adjust path if needed
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Clock,
  Users,
  DollarSign,
  MapPin,
  CalendarCheck,
  SearchX,
} from "lucide-react";


const DetailCard = ({ icon, label, value, colorClass = "text-primary-400" }) => (
  <div className="bg-surface/30 backdrop-blur-md border border-white/5 rounded-2xl p-6 transition-all hover:border-white/20 group">
    <div className={`mb-4 p-2 rounded-xl bg-white/5 w-fit group-hover:scale-110 transition-transform ${colorClass}`}>
      {icon}
    </div>
    <p className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-1">{label}</p>
    <p className="text-xl font-bold text-white tracking-tight">{value}</p>
  </div>
);

const ServiceDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const service = location.state?.service;
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) {
    return (
      <div className="bg-background text-white min-h-screen flex flex-col items-center justify-center text-center p-8">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="p-8 rounded-3xl bg-red-500/5 border border-red-500/10 mb-8"
        >
          <SearchX size={64} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-2 tracking-tight">Service Not Found</h1>
          <p className="text-gray-400 max-w-md mx-auto">
            The service you're looking for might have been retired or moved.
          </p>
        </motion.div>
        <button
          onClick={() => navigate(-1)}
          className="bg-white text-black font-bold px-10 py-4 rounded-full hover:bg-gray-200 transition-all flex items-center gap-3 shadow-xl hover:scale-105"
        >
          <ArrowLeft size={20} /> Return to Explore
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-background min-h-screen text-white font-sans selection:bg-primary-500 selection:text-white flex flex-col overflow-x-hidden">
        {/* Ambient Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
           <div className="absolute -top-[10%] -right-[10%] w-[800px] h-[800px] bg-primary-500/10 rounded-full blur-[160px] animate-pulse" />
           <div className="absolute -bottom-[10%] -left-[10%] w-[800px] h-[800px] bg-secondary-900/10 rounded-full blur-[160px]" />
        </div>

        <main className="flex-grow relative z-10">
          {/* Header & Hero Section */}
          <div className="relative">
             <div className="container mx-auto px-6 pt-12">
                <motion.button
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={() => navigate(-1)}
                  className="group flex items-center gap-3 text-sm text-gray-500 hover:text-white transition-all uppercase tracking-[0.2em] font-bold mb-16"
                >
                  <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-white/30 group-hover:bg-white/5 transition-all">
                    <ArrowLeft size={14} />
                  </div>
                  Back to Services
                </motion.button>

                <div className="max-w-4xl">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-[10px] font-bold uppercase tracking-[0.2em] mb-4">
                      Premium Service
                    </span>
                    <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-6 leading-tight text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40">
                      {service.name}
                    </h1>
                    <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-medium max-w-2xl mb-10">
                      {service.description}
                    </p>
                  </motion.div>
                </div>
             </div>
          </div>

          <div className="container mx-auto px-6 pb-24">
            {/* Quick Stats Bar */}
            <motion.div 
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.2 }}
               className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
            >
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 flex flex-col justify-center items-center text-center">
                    <DollarSign className="text-primary-400 mb-2" size={20} />
                    <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">Pricing</span>
                    <span className="text-xl font-black">{service.currency} {service.price}</span>
                </div>
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 flex flex-col justify-center items-center text-center">
                    <Clock className="text-indigo-400 mb-2" size={20} />
                    <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">Duration</span>
                    <span className="text-xl font-black">{service.duration_minutes}m</span>
                </div>
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 flex flex-col justify-center items-center text-center">
                    <MapPin className="text-emerald-400 mb-2" size={20} />
                    <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">Location</span>
                    <span className="text-xl font-black truncate w-full px-2">{service.location_mode}</span>
                </div>
                <div className="p-6 rounded-3xl bg-white/5 border border-white/10 flex flex-col justify-center items-center text-center">
                    <Users className="text-purple-400 mb-2" size={20} />
                    <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">Type</span>
                    <span className="text-xl font-black truncate w-full px-2">{service.session_type === "one2many" ? "Group" : "Individual"}</span>
                </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-16 items-start">
              {/* Content Sections */}
              <div className="lg:col-span-7 space-y-16">
                
                {/* Details Grid */}
                <section>
                  <div className="flex items-center gap-4 mb-10">
                    <h2 className="text-3xl font-black tracking-tight">Technical Details</h2>
                    <div className="h-[1px] flex-grow bg-white/10"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <DetailCard 
                      icon={<Users size={20} />} 
                      label="Session Availability" 
                      value={service.session_type === "one2many" ? "Group Session" : "Private 1:1"}
                      colorClass="text-indigo-400"
                    />
                    {service.session_type === "one2many" && (
                      <DetailCard 
                        icon={<Users size={20} />} 
                        label="Group Size" 
                        value={`${service.min_capacity} - ${service.max_capacity} people`}
                        colorClass="text-purple-400"
                      />
                    )}
                    <DetailCard 
                      icon={<MapPin size={20} />} 
                      label="Delivery Mode" 
                      value={service.location_mode}
                      colorClass="text-emerald-400"
                    />
                    <DetailCard 
                      icon={<Clock size={20} />} 
                      label="Time Commitment" 
                      value={`${service.duration_minutes} Minutes`}
                      colorClass="text-primary-400"
                    />
                  </div>
                </section>

                {/* Booking Rules */}
                {(service.booking_gap_days || service.min_booking_floor) && (
                  <section>
                    <div className="flex items-center gap-4 mb-10">
                      <h2 className="text-3xl font-black tracking-tight">Booking Policy</h2>
                      <div className="h-[1px] flex-grow bg-white/10"></div>
                    </div>
                    <div className="space-y-4">
                      {service.booking_gap_days && (
                        <div className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10">
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-orange-400/10 text-orange-400">
                              <CalendarCheck size={20} />
                            </div>
                            <div>
                                <p className="text-white font-bold">Preparation Gap</p>
                                <p className="text-sm text-gray-500">Minimum gap required between bookings</p>
                            </div>
                          </div>
                          <span className="text-lg font-black">{service.booking_gap_days} Days</span>
                        </div>
                      )}
                      {service.min_booking_floor && (
                        <div className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10">
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-blue-400/10 text-blue-400">
                              <CalendarCheck size={20} />
                            </div>
                            <div>
                                <p className="text-white font-bold">Advance Booking</p>
                                <p className="text-sm text-gray-500">Bookings must be made in advance</p>
                            </div>
                          </div>
                          <span className="text-lg font-black">{service.min_booking_floor} Days</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Portfolio */}
                {service.portfolio_urls?.length > 0 && (
                  <section>
                    <div className="flex items-center gap-4 mb-10">
                      <h2 className="text-3xl font-black tracking-tight">Our Work</h2>
                      <div className="h-[1px] flex-grow bg-white/10"></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                      {service.portfolio_urls.map((url, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, scale: 0.95 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          className="relative aspect-video rounded-3xl overflow-hidden group bg-white/5"
                        >
                            <img
                              src={url}
                              alt={`portfolio ${idx + 1}`}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                                <span className="text-sm font-bold uppercase tracking-widest text-white/80">Project {idx + 1}</span>
                            </div>
                        </motion.div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar Action Card */}
              <aside className="lg:col-span-5 mt-16 lg:mt-0">
                <div className="lg:sticky lg:top-12">
                   <motion.div
                     initial={{ opacity: 0, scale: 0.95 }}
                     animate={{ opacity: 1, scale: 1 }}
                     transition={{ delay: 0.3 }}
                     className="p-1 rounded-[3rem] bg-gradient-to-br from-white/20 via-white/5 to-transparent shadow-2xl"
                   >
                     <div className="bg-[#0f0f0f] rounded-[2.8rem] p-10 md:p-12 relative overflow-hidden">
                        {/* Decorative Background for the card */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/20 rounded-full blur-[60px] -mr-16 -mt-16" />
                        
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-gray-500 mb-8">Secure Booking</h3>
                        
                        <div className="flex items-baseline gap-2 mb-8">
                            <span className="text-5xl font-black tracking-tighter">{service.currency} {service.price}</span>
                            <span className="text-gray-500 font-bold text-sm">/ session</span>
                        </div>

                        <div className="space-y-6 mb-12">
                            <div className="flex items-center gap-4 text-gray-300">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                                    <Clock size={12} />
                                </div>
                                <span className="text-sm font-semibold">{service.duration_minutes} Minute Session</span>
                            </div>
                            <div className="flex items-center gap-4 text-gray-300">
                                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-500">
                                    <CalendarCheck size={12} />
                                </div>
                                <span className="text-sm font-semibold">Instant Confirmation</span>
                            </div>
                        </div>

                        <button
                          onClick={() => setShowBooking(true)}
                          className="w-full bg-white text-black font-black py-6 rounded-[2rem] text-xl hover:bg-primary-400 hover:text-white transition-all hover:scale-[1.02] active:scale-[0.98] shadow-2xl"
                        >
                          Book Session
                        </button>
                        
                        <p className="text-center text-gray-500 text-xs mt-8 leading-relaxed px-4">
                          Secure payment processing. You can manage and reschedule your session after booking.
                        </p>
                     </div>
                   </motion.div>
                </div>
              </aside>

            </div>
          </div>
        </main>
        
        <div className="container mx-auto px-6 py-12 border-t border-white/5 mt-auto">
          <AppFooter />
        </div>
      </div>

      <AnimatePresence>
        {showBooking && (
          <SlotBookingModal
            service={service}
            onClose={() => setShowBooking(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ServiceDetailsPage;
